import { UtilizationProductRow, UtilizationGroupRow, getUtilizationStatus } from './utilizationReport';
import { fmtBase } from './shared';

/**
 * Client-side CSV export of the ordered-vs-needed rows exactly as shown on the
 * Product Utilization report. Same numbers as the super-admin Data Export waste
 * section (both come from `computeUtilizationReport`), generated in the browser so
 * restaurant users can export without the super-admin route.
 */

const escapeCell = (value: string | number): string => {
  const s = String(value ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function buildUtilizationCsv(
  rows: UtilizationProductRow[],
  headers: string[]
): string {
  const lines = [headers.map(escapeCell).join(',')];
  for (const r of rows) {
    lines.push(
      [
        r.name,
        r.articleId ?? '',
        r.supplier,
        r.category ?? '',
        r.dishes.join(' | '),
        fmtBase(r.orderedBase, r.stdUnit),
        fmtBase(r.neededBase, r.stdUnit),
        r.utilizationPercent !== null ? `${r.utilizationPercent.toFixed(1)}%` : '',
        fmtBase(r.wasteBase, r.stdUnit),
        r.wasteEur !== null ? r.wasteEur.toFixed(2) : '',
        getUtilizationStatus(r.utilizationPercent),
        ...(headers.length > 11 ? [(r.potentialWasteEur ?? 0).toFixed(2), (r.restaurantContributions ?? []).reduce((sum, local) => sum + Math.max(0, -(local.wasteEur ?? 0)), 0).toFixed(2)] : []),
      ]
        .map(escapeCell)
        .join(',')
    );
  }
  return lines.join('\n');
}

/** CSV of a grouped tab (Dish grouped / Grouped): one line per group (aggregates;
 *  base cells blank when the group mixes units) followed by an indented line per
 *  member with its numbers. */
export function buildUtilizationGroupedCsv(groups: UtilizationGroupRow[], headers: string[]): string {
  const lines = [headers.map(escapeCell).join(',')];
  for (const g of groups) {
    lines.push(
      [
        g.primaryName,
        '',
        Array.from(new Set(g.members.map((m) => m.supplier))).join(' | '),
        '',
        g.dishes.join(' | '),
        g.orderedBase !== null && g.stdUnit !== null ? fmtBase(g.orderedBase, g.stdUnit) : '',
        g.neededBase !== null && g.stdUnit !== null ? fmtBase(g.neededBase, g.stdUnit) : '',
        g.utilizationPercent !== null ? `${g.utilizationPercent.toFixed(1)}%` : '',
        g.wasteBase !== null && g.stdUnit !== null ? fmtBase(g.wasteBase, g.stdUnit) : '',
        g.wasteEur !== null ? g.wasteEur.toFixed(2) : '',
        getUtilizationStatus(g.utilizationPercent),
      ]
        .map(escapeCell)
        .join(',')
    );
    for (const m of g.members) {
      lines.push(
        [
          `  - ${m.name}`,
          m.articleId ?? '',
          m.supplier,
          m.category ?? '',
          '',
          fmtBase(m.orderedBase, m.stdUnit),
          fmtBase(m.neededBase, m.stdUnit),
          m.utilizationPercent !== null ? `${m.utilizationPercent.toFixed(1)}%` : '',
          fmtBase(m.wasteBase, m.stdUnit),
          m.wasteEur !== null ? m.wasteEur.toFixed(2) : '',
          '',
        ]
          .map(escapeCell)
          .join(',')
      );
    }
  }
  return lines.join('\n');
}

/** Trigger a browser download of a CSV string. */
export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
